import { memo } from 'react';
import { FilterOption } from './constants';

interface FilterSectionProps {
  title: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  getCounts: (opt: FilterOption) => number;
  isLast?: boolean;
  isCustomMode?: boolean;
  relation?: 'and' | 'or';
  onRelationChange?: (relation: 'and' | 'or') => void;
  customColors?: Record<string, string>;
  onColorChange?: (value: string, color: string) => void;
}

const FilterSection = ({
  title,
  options,
  selected,
  onToggle,
  onSelectAll,
  onClear,
  getCounts,
  isLast,
  isCustomMode,
  relation,
  onRelationChange,
  customColors,
  onColorChange,
}: FilterSectionProps) => (
  <div className={`mb-4 ${!isLast ? 'border-b border-gray-100 pb-4' : ''}`}>
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700">{title}</span>
      <div className="flex items-center gap-2">
        {isCustomMode && onRelationChange && (
          <div className="flex rounded border border-gray-200 text-xs">
            <button
              onClick={() => onRelationChange('and')}
              className={`px-1.5 py-0.5 transition-colors ${
                relation === 'and'
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-500 hover:bg-gray-50'
              } rounded-l`}
            >
              AND
            </button>
            <button
              onClick={() => onRelationChange('or')}
              className={`px-1.5 py-0.5 transition-colors ${
                relation === 'or'
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-500 hover:bg-gray-50'
              } rounded-r border-l border-gray-200`}
            >
              OR
            </button>
          </div>
        )}
        <button onClick={onSelectAll} className="text-xs text-primary-600 hover:text-primary-700">
          All
        </button>
        <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-700">
          None
        </button>
      </div>
    </div>
    <div className="space-y-1">
      {options.map(opt => {
        const isSelected = selected.includes(opt.value);
        const count = getCounts(opt);
        const displayColor = customColors?.[opt.value] || opt.color;
        return (
          <div
            key={opt.value}
            className="flex items-center gap-2 rounded px-1 py-1 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggle(opt.value)}
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div className="relative">
              <input
                type="color"
                value={displayColor}
                onChange={e => onColorChange?.(opt.value, e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                title={`Change color for ${opt.label}`}
              />
              <span
                className="block h-4 w-4 cursor-pointer rounded-full border border-gray-300 transition-transform hover:scale-110"
                style={{ backgroundColor: displayColor }}
              />
            </div>
            <span
              className="flex-1 cursor-pointer text-sm text-gray-700"
              onClick={() => onToggle(opt.value)}
            >
              {opt.label}
            </span>
            <span className="text-xs text-gray-400">{count}</span>
          </div>
        );
      })}
    </div>
  </div>
);

export default memo(FilterSection);
